/**

 * Telemetria interna da fila (debug / observabilidade).

 */



import {
  getUnifiedPressureLevel,
  setReplayCursorLagHint,
  setStoragePressureHints,
  setRetryStormPerMinHint,
} from './runtime/pressureModel';

import { getQueueMutable } from './queue/queueState';

import { getLastAckedSequenceSync } from './replay/persistentReplay';



export interface QueueTelemetry {

  queue_size: number;

  retry_count: number;

  flush_duration_ms: number;

  failed_batches: number;

  dead_letter_count: number;

  dropped_events: number;

  duplicated_events: number;

  ambiguous_ack_count: number;

  orphan_processing_count: number;

  replay_batches: number;

  ordering_inversions: number;

  storage_bytes_used: number;

  evicted_count: number;

  last_flush_at: number | null;

  storage_commit_latency_ms: number;

  storage_fallback_count: number;

}



export interface StructuredTelemetrySnapshot {

  queue: {

    size: number;

    pressure: number;

    partitionSizes: Record<string, number>;

  };

  replay: {

    batches: number;

    inversions: number;

    cursorLag: number;

  };

  retry: {

    byType: Record<string, number>;

    total: number;

  };

  storage: {

    backend: string;

    bytes: number;

    commitLatencyMs: number;

    healthOk: boolean;

    fallbackCount: number;

    latencyP95Ms?: number;

  };

  coordination: {

    isLeader: boolean;

    lockHolder: string | null;

    epoch: number;

    fencingToken: number;

    staleLeaderRecoveries?: number;

    orphanItemsReset?: number;

  };

  flush: {

    durationMs: number;

    lastAt: number | null;

  };

  recovery?: {

    lastPhases: string[];

    runs: number;

  };

  adaptive?: {

    pressure: number;

  };

  runtime?: {

    retryStormPerMin: number;

    enqueueStormPerMin: number;

  };

}



const telemetry: QueueTelemetry = {

  queue_size: 0,

  retry_count: 0,

  flush_duration_ms: 0,

  failed_batches: 0,

  dead_letter_count: 0,

  dropped_events: 0,

  duplicated_events: 0,

  ambiguous_ack_count: 0,

  orphan_processing_count: 0,

  replay_batches: 0,

  ordering_inversions: 0,

  storage_bytes_used: 0,

  evicted_count: 0,

  last_flush_at: null,

  storage_commit_latency_ms: 0,

  storage_fallback_count: 0,

};



let storageBackend = 'unknown';

let storageHealthOk = true;

let storageLatencyP95 = 0;

const storageLatencySamples: number[] = [];

let coordinationSnapshot: StructuredTelemetrySnapshot['coordination'] = {

  isLeader: true,

  lockHolder: null,

  epoch: 0,

  fencingToken: 0,

};

let partitionSizesSnapshot: Record<string, number> = {};

let orphanItemsResetTotal = 0;

let staleLeaderRecoveries = 0;
let leaderChanges = 0;
let lockConflicts = 0;
let fencingRejections = 0;
let epochInvalidations = 0;
let epochChanges = 0;

let recoveryRuns = 0;

let lastRecoveryPhases: string[] = [];

const retryTimestamps: number[] = [];

const enqueueTimestamps: number[] = [];



const retryByType = new Map<string, number>();



const timelines = new Map<

  string,

  Array<{ phase: string; at: number }>

>();



function countInWindow(timestamps: number[], windowMs: number): number {

  const cutoff = Date.now() - windowMs;

  while (timestamps.length > 0 && timestamps[0] < cutoff) {

    timestamps.shift();

  }

  return timestamps.length;

}



function computeReplayCursorLag(): number {

  let maxLag = 0;

  for (const item of getQueueMutable()) {

    const ack = getLastAckedSequenceSync(item.params.sessionId);

    const lag = Math.max(0, item.sessionSequence - ack);

    if (lag > maxLag) maxLag = lag;

  }

  return maxLag;

}



export function getQueueTelemetry(): Readonly<QueueTelemetry> {

  return { ...telemetry };

}



export function recordQueueSize(size: number): void {

  telemetry.queue_size = size;

}



export function recordEnqueueStorm(): void {

  enqueueTimestamps.push(Date.now());

}



export function recordFlushDuration(ms: number): void {

  telemetry.flush_duration_ms = ms;

  telemetry.last_flush_at = Date.now();

}



export function recordRetry(): void {

  telemetry.retry_count++;

  retryTimestamps.push(Date.now());

}



export function recordFailedBatch(): void {

  telemetry.failed_batches++;

}



export function recordDropped(count = 1): void {

  telemetry.dropped_events += count;

}



export function recordDuplicated(count: number): void {

  telemetry.duplicated_events += count;

}



export function recordAmbiguousAck(count = 1): void {

  telemetry.ambiguous_ack_count += count;

}



export function recordOrphanProcessing(count = 1): void {

  telemetry.orphan_processing_count += count;

}



export function recordStaleLeaderRecovery(): void {

  staleLeaderRecoveries++;

}

export function recordLeaderChange(): void {
  leaderChanges++;
}

export function recordLockConflict(): void {
  lockConflicts++;
}

export function recordFencingRejection(): void {
  fencingRejections++;
}

export function recordEpochInvalidation(): void {
  epochInvalidations++;
}

export function recordEpochChange(): void {
  epochChanges++;
}

export function recordOwnershipDenied(): void {
  ownershipDenied++;
  pushConflict({ type: 'ownership_denied', at: Date.now() });
}

let ownershipDenied = 0;
let replayDeduped = 0;
let recoveryPlanned = 0;
let recoveryStarted = 0;
let recoveryFailed = 0;
let recoveryRollback = 0;
let storageCorruptionDetected = 0;
let runtimeModeChanges = 0;
let currentRuntimeMode = 'normal';

export interface DebugRingEvent {
  type: string;
  at: number;
  detail?: string;
}

const lastConflicts: DebugRingEvent[] = [];
const lastRecoveries: DebugRingEvent[] = [];
const MAX_RING = 20;

function pushConflict(ev: DebugRingEvent): void {
  lastConflicts.push(ev);
  if (lastConflicts.length > MAX_RING) lastConflicts.shift();
}

function pushRecovery(ev: DebugRingEvent): void {
  lastRecoveries.push(ev);
  if (lastRecoveries.length > MAX_RING) lastRecoveries.shift();
}

export function getLastConflicts(): readonly DebugRingEvent[] {
  return lastConflicts;
}

export function getLastRecoveries(): readonly DebugRingEvent[] {
  return lastRecoveries;
}

export function recordReplayDeduped(count = 1): void {
  replayDeduped += count;
}

export function recordRecoveryPlanned(): void {
  recoveryPlanned++;
  pushRecovery({ type: 'planned', at: Date.now() });
}

export function recordRecoveryStarted(): void {
  recoveryStarted++;
  pushRecovery({ type: 'started', at: Date.now() });
}

export function recordRecoveryFailed(reason?: string): void {
  recoveryFailed++;
  pushRecovery({ type: 'failed', at: Date.now(), detail: reason });
}

export function recordRecoveryRollback(): void {
  recoveryRollback++;
  pushRecovery({ type: 'rollback', at: Date.now() });
}

let recoveryEscalationCount = 0;
let lastRecoveryEscalationLevel = 'soft';

export function recordRecoveryEscalation(level: string): void {
  recoveryEscalationCount++;
  lastRecoveryEscalationLevel = level;
  pushRecovery({ type: 'escalation', at: Date.now(), detail: level });
}

export function getRecoveryEscalationTelemetry(): Readonly<{
  count: number;
  lastLevel: string;
}> {
  return { count: recoveryEscalationCount, lastLevel: lastRecoveryEscalationLevel };
}

export function recordStorageCorruptionDetected(): void {
  storageCorruptionDetected++;
  pushConflict({ type: 'storage_corruption', at: Date.now() });
}

export function recordRuntimeModeChange(mode: string): void {
  if (currentRuntimeMode !== mode) {
    currentRuntimeMode = mode;
    runtimeModeChanges++;
  }
}

export function getRuntimeModeTelemetry(): Readonly<{
  mode: string;
  changes: number;
}> {
  return { mode: currentRuntimeMode, changes: runtimeModeChanges };
}

export function getExtendedEventCounts(): Readonly<{
  ownershipDenied: number;
  replayDeduped: number;
  recoveryPlanned: number;
  recoveryStarted: number;
  recoveryFailed: number;
  recoveryRollback: number;
  storageCorruptionDetected: number;
  recoveryEscalations: number;
}> {
  return {
    ownershipDenied,
    replayDeduped,
    recoveryPlanned,
    recoveryStarted,
    recoveryFailed,
    recoveryRollback,
    storageCorruptionDetected,
    recoveryEscalations: recoveryEscalationCount,
  };
}

export function getCoordinationEventCounts(): Readonly<{
  leaderChanges: number;
  lockConflicts: number;
  fencingRejections: number;
  epochInvalidations: number;
  epochChanges: number;
}> {
  return {
    leaderChanges,
    lockConflicts,
    fencingRejections,
    epochInvalidations,
    epochChanges,
  };
}

let replayStarted = 0;
let replayCompleted = 0;
let replayFailed = 0;
let replayRolledBack = 0;
let shadowCommitFailed = 0;
let generationSwapFailed = 0;
let partitionEvictions = 0;
const partitionFlushLatency: Record<string, number> = {};
const partitionRetries: Record<string, number> = {};
const adaptiveDecisions: Array<{ domain: string; action: string; at: number }> = [];

export function recordReplayStarted(): void {
  replayStarted++;
}
export function recordReplayResumed(): void {
  replayStarted++;
}
export function recordReplayCompleted(): void {
  replayCompleted++;
}
export function recordReplayFailed(): void {
  replayFailed++;
}
export function recordReplayRolledBack(): void {
  replayRolledBack++;
}
export function recordShadowCommitFailed(): void {
  shadowCommitFailed++;
}
export function recordGenerationSwapFailed(): void {
  generationSwapFailed++;
}
export function recordPartitionEviction(partition: string, count = 1): void {
  partitionEvictions += count;
  partitionRetries[partition] = (partitionRetries[partition] ?? 0) + count;
}
export function recordPartitionFlushLatency(partition: string, ms: number): void {
  partitionFlushLatency[partition] = ms;
}
export function recordPartitionRetry(partition: string): void {
  partitionRetries[partition] = (partitionRetries[partition] ?? 0) + 1;
}
export function recordAdaptiveDecision(domain: string, action: string): void {
  adaptiveDecisions.push({ domain, action, at: Date.now() });
  if (adaptiveDecisions.length > 50) adaptiveDecisions.shift();
}

export function getReplayEventCounts(): Readonly<{
  started: number;
  completed: number;
  failed: number;
  rolledBack: number;
}> {
  return {
    started: replayStarted,
    completed: replayCompleted,
    failed: replayFailed,
    rolledBack: replayRolledBack,
  };
}



export function recordOrphanProcessingReset(count = 1): void {

  orphanItemsResetTotal += count;

  recordOrphanProcessing(count);

}



export function getStaleLeaderRecoveries(): number {

  return staleLeaderRecoveries;

}



export function recordReplayBatch(): void {

  telemetry.replay_batches++;

}



export function recordOrderingInversion(count = 1): void {

  telemetry.ordering_inversions += count;

}



export function recordStorageBytes(bytes: number): void {

  telemetry.storage_bytes_used = bytes;

}



export function recordEvicted(count = 1): void {

  telemetry.evicted_count += count;

}



export function recordRetryByType(eventType: string): void {

  retryByType.set(eventType, (retryByType.get(eventType) ?? 0) + 1);

}



export function getRetryByType(): Readonly<Record<string, number>> {

  return Object.fromEntries(retryByType);

}



export function recordDeadLetterCount(count: number): void {

  telemetry.dead_letter_count = count;

}



export function recordEventPhase(eventId: string, phase: string): void {

  if (!timelines.has(eventId)) timelines.set(eventId, []);

  timelines.get(eventId)!.push({ phase, at: Date.now() });

}



export function getEventTimeline(

  eventId: string

): ReadonlyArray<{ phase: string; at: number }> {

  return timelines.get(eventId) ?? [];

}



export function recordStorageCommitLatency(ms: number): void {

  telemetry.storage_commit_latency_ms = ms;

  storageLatencySamples.push(ms);

  if (storageLatencySamples.length > 50) storageLatencySamples.shift();

  const sorted = [...storageLatencySamples].sort((a, b) => a - b);

  const idx = Math.floor(sorted.length * 0.95);

  storageLatencyP95 = sorted[idx] ?? ms;

}



export function recordStorageFallback(): void {

  telemetry.storage_fallback_count++;

}



export function recordStorageHealth(backend: string, ok: boolean): void {

  storageBackend = backend;

  storageHealthOk = ok;

}



export function recordRecoveryPhase(phase: string): void {

  lastRecoveryPhases.push(phase);

}



export function recordRecoveryRunComplete(): void {

  recoveryRuns++;

}



export function updateCoordinationTelemetry(

  patch: Partial<StructuredTelemetrySnapshot['coordination']>

): void {

  coordinationSnapshot = { ...coordinationSnapshot, ...patch };

}



export function updatePartitionSizes(sizes: Record<string, number>): void {

  partitionSizesSnapshot = { ...sizes };

}



export function getStructuredTelemetry(): StructuredTelemetrySnapshot {
  const cursorLag = computeReplayCursorLag();
  setReplayCursorLagHint(cursorLag);
  setStoragePressureHints(telemetry.storage_fallback_count, storageHealthOk);
  setRetryStormPerMinHint(countInWindow(retryTimestamps, 60_000));

  return {

    queue: {

      size: telemetry.queue_size,

      pressure: getUnifiedPressureLevel(),

      partitionSizes: { ...partitionSizesSnapshot },

    },

    replay: {

      batches: telemetry.replay_batches,

      inversions: telemetry.ordering_inversions,

      cursorLag: computeReplayCursorLag(),

    },

    retry: {

      byType: getRetryByType(),

      total: telemetry.retry_count,

    },

    storage: {

      backend: storageBackend,

      bytes: telemetry.storage_bytes_used,

      commitLatencyMs: telemetry.storage_commit_latency_ms,

      healthOk: storageHealthOk,

      fallbackCount: telemetry.storage_fallback_count,

      latencyP95Ms: storageLatencyP95,

    },

    coordination: {

      ...coordinationSnapshot,

      staleLeaderRecoveries,

      orphanItemsReset: orphanItemsResetTotal,

    },

    flush: {

      durationMs: telemetry.flush_duration_ms,

      lastAt: telemetry.last_flush_at,

    },

    recovery: {

      lastPhases: [...lastRecoveryPhases],

      runs: recoveryRuns,

    },

    adaptive: {

      pressure: getUnifiedPressureLevel(),

    },

    runtime: {

      retryStormPerMin: countInWindow(retryTimestamps, 60_000),

      enqueueStormPerMin: countInWindow(enqueueTimestamps, 60_000),

    },

  };

}



export function _testResetTelemetry(): void {

  telemetry.queue_size = 0;

  telemetry.retry_count = 0;

  telemetry.flush_duration_ms = 0;

  telemetry.failed_batches = 0;

  telemetry.dead_letter_count = 0;

  telemetry.dropped_events = 0;

  telemetry.duplicated_events = 0;

  telemetry.ambiguous_ack_count = 0;

  telemetry.orphan_processing_count = 0;

  telemetry.replay_batches = 0;

  telemetry.ordering_inversions = 0;

  telemetry.storage_bytes_used = 0;

  telemetry.evicted_count = 0;

  telemetry.last_flush_at = null;

  telemetry.storage_commit_latency_ms = 0;

  telemetry.storage_fallback_count = 0;

  storageBackend = 'unknown';

  storageHealthOk = true;

  storageLatencyP95 = 0;

  storageLatencySamples.length = 0;

  coordinationSnapshot = {

    isLeader: true,

    lockHolder: null,

    epoch: 0,

    fencingToken: 0,

  };

  partitionSizesSnapshot = {};

  orphanItemsResetTotal = 0;

  staleLeaderRecoveries = 0;
  leaderChanges = 0;
  lockConflicts = 0;
  fencingRejections = 0;
  epochInvalidations = 0;
  epochChanges = 0;
  ownershipDenied = 0;
  replayDeduped = 0;
  recoveryPlanned = 0;
  recoveryStarted = 0;
  recoveryFailed = 0;
  recoveryRollback = 0;
  recoveryEscalationCount = 0;
  lastRecoveryEscalationLevel = 'soft';
  storageCorruptionDetected = 0;
  runtimeModeChanges = 0;
  currentRuntimeMode = 'normal';
  lastConflicts.length = 0;
  lastRecoveries.length = 0;

  recoveryRuns = 0;

  lastRecoveryPhases = [];

  retryTimestamps.length = 0;

  enqueueTimestamps.length = 0;

  timelines.clear();

  retryByType.clear();

  replayStarted = 0;
  replayCompleted = 0;
  replayFailed = 0;
  replayRolledBack = 0;
  shadowCommitFailed = 0;
  generationSwapFailed = 0;
  partitionEvictions = 0;
  for (const k of Object.keys(partitionRetries)) delete partitionRetries[k];
  for (const k of Object.keys(partitionFlushLatency)) delete partitionFlushLatency[k];
  adaptiveDecisions.length = 0;

}


